import { ApolloError, gql } from '@apollo/client';
import { ApolloQueryResult } from '@apollo/client/core/types';
import { GraphQLErrors } from '@apollo/client/errors';
import { Box, VStack } from '@chakra-ui/react';
// import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { isNil } from 'lodash';
import isNumber from 'lodash/isNumber';
import uniq from 'lodash/uniq';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '~auth/useAuth';
// import { Shield } from '~auth/Shield';
import {
  DisplayTableServerSide,
  useDisplayTableServerSide,
} from '~components/displays/DisplayTable';
import { FilterOptionServerSide } from '~components/displays/DisplayTable/constants';
import {
  DisplayTableContext,
  DisplayTableProvider,
} from '~components/displays/DisplayTable/DisplayTableContext';
import {
  FilterContext,
  getPersistentFilters,
} from '~components/displays/DisplayTable/persistantFilters/persistantFilters';
import { EmptyConfigurationView, EmptyView, EmptyViewButton } from '~components/EmptyView';
import { EmptyDisplayConfigurationIllustration } from '~components/ui/illustrations/EmptyDisplayConfigurationIllustration';
import { Page } from '~components/ui/Page';
import { PageActions } from '~components/ui/PageActions';
import { PageContent } from '~components/ui/PageContentConfiguration';
import { PageLoader } from '~components/ui/PageLoader';
import { useConditionalPolling } from '~components/useConditionalPolling';
import { Exact } from '~graphql/__generated__/types';
import { HandleApiError } from '~graphql/HandleApiError';
import { useAnalyticsReporter } from '~utils/analytics';
// import { fromError } from '~utils/errors';
import { useSubscriptionType } from '~utils/subscriptions';
import { ensure, isDefined } from '~utils/types';
import {
  ConfigurationDisplaysPageQuery,
  // useAssignConfigurationMutation,
  useConfigurationDisplaysPageQuery,
} from './__generated__/ConfigDevices.graphql';

export type DataWithCustomer = Omit<ConfigurationDisplaysPageQuery, 'customer'> & {
  customer: ConfigurationDisplaysPageQuery['customer'];
};

export type DisplayFilters = Array<{ id: string; value: FilterOptionServerSide[] }>;

type CustomerDisplaysOverviewPropsServerSide = {
  data: DataWithCustomer;
  error: ApolloError | undefined;
  refetch: (
    variables?: Partial<Exact<{ customerHandle: string }>>,
  ) => Promise<ApolloQueryResult<ConfigurationDisplaysPageQuery>>;
  pageIndex: number;
  pageSize: number;
  filters: DisplayFilters;
  totalRowCount: number;
  totalDisplays: number;
  search: string;
  handleSetSearch: (searchText: string) => void;
  handleSetPageSize: (pageSize: number) => void;
  handleSetPageIndex: (pageIndex: number) => void;
  handleSetFilters: (
    filterId: string,
    newValue: FilterOptionServerSide[],
    resetPagination?: boolean,
  ) => void;
  setIncludeConfigurationId: React.Dispatch<React.SetStateAction<boolean>>;
};

function DisplayOverviewServerSide({
  data,
  error,
  // refetch,
  pageIndex,
  pageSize,
  filters,
  handleSetPageSize,
  handleSetPageIndex,
  totalRowCount,
  totalDisplays,
  handleSetFilters,
  search,
  handleSetSearch,
}: // setIncludeConfigurationId,
CustomerDisplaysOverviewPropsServerSide) {
  // const { customer } = useParams();
  const navigate = useNavigate();
  const analytics = useAnalyticsReporter();
  const { hasWaveSubscription } = useSubscriptionType(data.customer);

  const table = useDisplayTableServerSide({
    data,
    pageIndex,
    pageSize,
    totalRowCount,
    hasWaveSubscription,
  });

  const tableContext = useContext(DisplayTableContext);

  // const selectedCount = table.selectedFlatRows.length;

  if (isNil(tableContext)) {
    throw new Error('TableContext can only be used within TableContext Provider');
  }

  const { setTable } = tableContext;

  useEffect(() => {
    setTable?.(table);
  }, [table, setTable]);
  // const toast = useToast();

  // const { configurationId } = useParams();

  async function handleDisplayClicked(id: string) {
    analytics.track('displayDetail');
    navigate(`../../displays/${id}`);
  }
  async function handleAssignDisplayClicked() {
    navigate(`../../displays/`);
  }
  // const [AssignConfigurationMutation] = useAssignConfigurationMutation();
  // const displayIds = table.selectedFlatRows.map((row) => row.original.id);
  // const handleBulkAssignConfiguration = useCallback(async () => {
  //   try {
  //     if (configurationId) {
  //       const response = await AssignConfigurationMutation({
  //         variables: {
  //           input: {
  //             displayIds,
  //             id: configurationId,
  //           },
  //         },
  //       });

  //       if (response?.data?.assignConfiguration) {
  //         setIncludeConfigurationId(true);
  //         refetch();
  //       }
  //       if (displayIds.length === 1) {
  //         analytics.track('configurationUpdate');
  //       } else {
  //         analytics.track('displayBulkActionComplete', {
  //           action: 'updateConfiguration',
  //           displayCount: displayIds.length,
  //         });
  //       }
  //     }
  //   } catch (err) {
  //     toast({
  //       status: 'error',
  //       title: t('configurationUpdateError'),
  //       description: fromError(err, 'AssignConfiguration'),
  //     });
  //   }
  // }, [
  //   configurationId,
  //   AssignConfigurationMutation,
  //   displayIds,
  //   setIncludeConfigurationId,
  //   refetch,
  //   analytics,
  //   toast,
  // ]);

  const extractInvalidDisplayIds = (graphQLErrors: GraphQLErrors | undefined): string[] => {
    if (!graphQLErrors) {
      return [];
    }

    const invalidDisplayIds = graphQLErrors?.map(({ path }) => {
      if (!path) {
        return;
      }

      const [c, d, index] = path;
      if (c === 'customer' && d === 'displays' && isNumber(index)) {
        return data?.customer?.displays[index as number]?.id;
      }
    });

    return uniq(invalidDisplayIds?.filter((id) => isDefined(id))) as string[];
  };

  return (
    <>
      {totalDisplays > 0 && (
        <PageActions
          left={
            <>
              <DisplayTableServerSide.Filter
                table={table}
                data={data}
                filtersState={filters}
                handleSetFilters={handleSetFilters}
              />
              {/* <Shield requiredPermissions={[Permission.DisplayProfileUpdate]}>
                <Button
                  variant="solid"
                  bgColor="inherit"
                  border="1px solid"
                  borderRadius="md"
                  borderColor="gray.100"
                  isDisabled={selectedCount ? false : true}
                  onClick={handleBulkAssignConfiguration}
                >
                  {t('assignConfiguration')}
                </Button>
              </Shield> */}
            </>
          }
          searchbar={
            <DisplayTableServerSide.SearchSelector
              search={search}
              handleSetSearch={handleSetSearch}
            />
          }
        />
      )}

      <Box
        boxShadow="elevated"
        border="1px solid"
        borderRadius="md"
        borderColor="gray.100"
        overflowX="auto"
        bgColor="white"
      >
        {totalDisplays === 0 ? (
          <VStack paddingTop={20} paddingBottom={20} spacing={8}>
            <EmptyConfigurationView
              icon={<EmptyDisplayConfigurationIllustration />}
              title={t('noDisplaysAssigned')}
              description={t('assignDisplayToConfig')}
              padding="0"
            >
              <EmptyViewButton
                label={t('assignDisplays')}
                onClick={() => {
                  handleAssignDisplayClicked();
                }}
              />
            </EmptyConfigurationView>
          </VStack>
        ) : (
          <DisplayTableServerSide
            table={table}
            onGoToDetail={(id) => handleDisplayClicked(id)}
            invalidDisplayIds={extractInvalidDisplayIds(error?.graphQLErrors)}
          />
        )}
      </Box>
      <Box display="flex" marginTop="4">
        <Box flex="1">
          <DisplayTableServerSide.PageSizeSelector
            table={table}
            handleSetPageSize={handleSetPageSize}
            pageSize={pageSize}
          />
        </Box>
        <DisplayTableServerSide.Pagination
          pageIndex={pageIndex}
          totalRowCount={totalRowCount}
          handleSetPageIndex={handleSetPageIndex}
          pageSize={pageSize}
        />
      </Box>
    </>
  );
}

const POLL_INTERVAL = 10000;

export function ConfigDevices() {
  const { customer } = useParams();
  const { user } = useAuth();
  const [pageSize, setPageSize] = useState(25);
  const [pageIndex, setPageIndex] = useState(0);
  const navigate = useNavigate();
  const { configurationId } = useParams();
  const [includeConfigurationId, setIncludeConfigurationId] = useState(true);
  const [search, setSearch] = useState('');

  // Track whether initial persisted filters have been applied
  const [isFiltersInitialized, setIsFiltersInitialized] = useState(false);
  const filtersAppliedRef = useRef(false);
  const [filters, setFilters] = useState<DisplayFilters>([
    //starts here
    { id: 'firmware', value: [] },
    { id: 'powerSchedule', value: [] },
    { id: 'playlist', value: [] },
    { id: 'groups', value: [] },
    { id: 'warnings', value: [] },
    { id: 'site', value: [] },
    { id: 'status', value: [] },
    { id: 'configuration', value: [] },
  ]);
  interface TransformedResult {
    firmwares: string[];
    groupIds: string[];
    playlistIds: string[];
    scheduleIds: string[];
    siteIds: string[];
    status: string[];
    warnings: string[];
    configurationIds: string[];
  }
  // Transform function with proper types
  function transformFiltersData(filters: DisplayFilters): TransformedResult {
    const transformed: TransformedResult = {
      firmwares: [],
      groupIds: [],
      playlistIds: [],
      scheduleIds: [],
      siteIds: [],
      status: [],
      warnings: [],
      configurationIds: [],
    };
    if (configurationId && includeConfigurationId) {
      transformed.configurationIds.push(configurationId);
    }
    filters.forEach((filter) => {
      switch (filter.id) {
        case 'firmware':
          transformed.firmwares = filter.value.map((option) => option.id);
          break;
        case 'groups':
          transformed.groupIds = filter.value.map((option) => option.id);
          break;
        case 'playlist':
          transformed.playlistIds = filter.value.map((option) => option.id);
          break;
        case 'powerSchedule':
          transformed.scheduleIds = filter.value.map((option) => option.id);
          break;
        case 'site':
          transformed.siteIds = filter.value.map((option) => option.id);
          break;
        case 'warnings':
          transformed.warnings = filter.value.map((option) => option.id);
          break;
        case 'status':
          transformed.status = filter.value.map((option) => option.id);
          break;
        // case 'configuration':
        //   transformed.configurationIds = filter.value.map((option) => option.id);
        //   break;
        default:
          break;
      }
    });

    return transformed;
  }
  const transformedData = transformFiltersData(filters);

  const handleSetPageSize = (pageSize: number) => {
    setPageSize(pageSize);
    setPageIndex(0);
  };
  const handleSetPageIndex = (pageIndex: number) => {
    setPageIndex(pageIndex);
  };

  const handleSetFilters = (
    filterId: string,
    newValue: FilterOptionServerSide[],
    resetPagination = true,
  ) => {
    if (resetPagination) {
      setPageIndex(0);
    }
    setFilters((prevFilters) =>
      prevFilters.map((filter) =>
        filter.id === filterId ? { ...filter, value: newValue } : filter,
      ),
    );
  };

  function handleAssignDisplayClicked() {
    navigate(`../../displays/`);
  }
  const handleSetSearch = (searchText: string) => {
    setPageIndex(0);
    setSearch(searchText);
  };

  // Load persisted filters from localStorage using customer handle (from URL)
  // This runs BEFORE the query, using URL handle instead of GraphQL customer.id
  useEffect(() => {
    // Only run once per customer
    if (filtersAppliedRef.current || !customer || !user?.subId) {
      if (!customer) {
        setIsFiltersInitialized(true);
      }
      return;
    }

    async function loadAndApplyFilters() {
      if (!user?.subId || !customer) return;

      // Use customer handle from URL (not customer.id from GraphQL)
      const persisted = await getPersistentFilters(
        user.subId,
        customer,
        FilterContext.CONFIGURATION,
      );

      // If we have persisted filters, apply them
      if (Object.keys(persisted).length > 0) {
        setFilters((prevFilters) =>
          prevFilters.map((filter) => {
            const persistedValues = persisted[filter.id];
            if (persistedValues && persistedValues.length > 0) {
              return {
                ...filter,
                value: persistedValues.map((v) => ({
                  ...v,
                  id: v.id,
                  value: v.id,
                  column: filter.id,
                })) as FilterOptionServerSide[],
              };
            }
            return filter;
          }),
        );
      }

      filtersAppliedRef.current = true;
      setIsFiltersInitialized(true);
    }

    loadAndApplyFilters();
  }, [customer, user]);

  const { data, loading, error, refetch, startPolling, stopPolling } =
    useConfigurationDisplaysPageQuery({
      variables: {
        customerHandle: ensure(customer),
        pageNumber: pageIndex,
        pageSize: pageSize,
        searchTerm: search,
        filter: transformedData,
      },
      skip: !isFiltersInitialized, // Skip query until filters are loaded
      pollInterval: POLL_INTERVAL,
      errorPolicy: 'all',
    });

  useConditionalPolling({ startPolling, stopPolling, pollInterval: POLL_INTERVAL });

  const totalRowCount = data?.customer?.totalFilteredDisplaysCount || 0;

  const totalDisplays = data?.customer?.totalDisplaysCount || 0;

  return (
    <Page title="Displays" pageName="displays_overview">
      <DisplayTableProvider>
        <PageContent>
          {loading || !isFiltersInitialized ? (
            <PageLoader />
          ) : error ? (
            <HandleApiError error={error} />
          ) : isDefined(data?.customer) ? (
            totalDisplays === 0 ? (
              <VStack paddingTop={20} paddingBottom={20} spacing={8}>
                <EmptyView
                  icon={<EmptyDisplayConfigurationIllustration />}
                  title={t('noDisplaysAssigned')}
                  description={t('noDisplaysAssignedDesc')}
                  padding="0"
                >
                  <EmptyViewButton
                    label={t('assignDisplays')}
                    onClick={() => handleAssignDisplayClicked()}
                    isDisabled={false}
                  />
                </EmptyView>
              </VStack>
            ) : (
              <DisplayOverviewServerSide
                data={data as DataWithCustomer}
                error={error}
                refetch={refetch}
                pageSize={pageSize}
                pageIndex={pageIndex}
                totalRowCount={totalRowCount}
                totalDisplays={totalDisplays}
                filters={filters}
                search={search}
                handleSetSearch={handleSetSearch}
                handleSetPageSize={handleSetPageSize}
                handleSetPageIndex={handleSetPageIndex}
                handleSetFilters={handleSetFilters}
                setIncludeConfigurationId={setIncludeConfigurationId}
              />
            )
          ) : (
            <EmptyView
              icon={<EmptyDisplayConfigurationIllustration />}
              title={t('noDisplaysAssigned')}
              description={t('noDisplaysAssignedDesc')}
              padding="0"
            >
              <EmptyViewButton
                label={t('assignDisplays')}
                onClick={() => handleAssignDisplayClicked()}
                isDisabled={false}
              />
            </EmptyView>
          )}
        </PageContent>
      </DisplayTableProvider>
    </Page>
  );
}

ConfigDevices.graphql = {
  mutations: {
    BulkAssignConfiguration: gql`
      mutation assignConfiguration($input: DisplayBulkAssignConfigurationsInput!) {
        assignConfiguration(input: $input)
      }
    `,
  },
  queries: {
    ConfigurationSummary: gql`
      query ConfigurationDisplaysPage(
        $customerHandle: String!
        $pageNumber: Int
        $pageSize: Int
        $searchTerm: String
        $filter: DisplayFilters
      ) {
        customer: customerByHandle(handle: $customerHandle) {
          id
          name
          ...BulkActionModal_customer
          displays(
            pageNumber: $pageNumber
            pageSize: $pageSize
            searchTerm: $searchTerm
            filter: $filter
          ) {
            id
            serialNumber
            agentVersion
            networkInformation {
              ethernetMacAddress
              wifiMacAddress
              localIPAddress
            }
            ...DisplayTable_display
            ...BulkActionModal_display
          }
          supportedPlatforms
          configurations {
            id
            name
            state
            platform
          }
          assignedConfigurations {
            id
            name
            state
          }
          displayCount
          warningList {
            key
            label
          }
          totalDisplaysCount(filter: $filter)
          totalFilteredDisplaysCount(
            pageNumber: $pageNumber
            pageSize: $pageSize
            searchTerm: $searchTerm
            filter: $filter
          )
          waveSubscription {
            usage {
              current
              max
            }
          }
          displayUsage {
            presence {
              connected
              disconnected
            }
            powerState {
              plannedOn
              plannedStandBy
              unplannedOn
              unplannedStandBy
              disconnected
            }
            contentSource {
              standby
              disconnected
              sources {
                source
                count
              }
            }
          }
          displayFirmwareVersions
        }
        organization {
          ...BulkActionModal_organization
        }
      }
    `,
  },
};
